import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { recoverRequestsApi, type EstatePartInput } from './api'

/**
 * Parts of the estate (backend lib/estate-parts.js): what a recovery scenario sends each part to.
 * Changing a part changes what every scenario's plan means, so the scenarios are read again too.
 */
export const partsKey = ['recover-requests', 'scenarios', 'parts'] as const

export function useEstateParts(enabled = true) {
    return useQuery({ queryKey: partsKey, queryFn: async () => (await recoverRequestsApi.listParts()).data.data ?? [], enabled })
}
export function useEstatePartOptions(enabled = true) {
    return useQuery({ queryKey: ['recover-requests', 'scenarios', 'part-options'], queryFn: async () => (await recoverRequestsApi.partOptions()).data.data, enabled })
}
export function useScenarioTemplates(enabled = true) {
    return useQuery({ queryKey: ['recover-requests', 'scenarios', 'templates'], queryFn: async () => (await recoverRequestsApi.scenarioTemplates()).data.data, enabled, staleTime: Infinity })
}

function useRefreshParts() {
    const queryClient = useQueryClient()
    return () => Promise.all([
        queryClient.invalidateQueries({ queryKey: partsKey }),
        queryClient.invalidateQueries({ queryKey: ['recoveryScenarios'] }),
        queryClient.invalidateQueries({ queryKey: ['recover-requests', 'scenarios'] }),
    ])
}
export function useSaveEstatePart() {
    const refresh = useRefreshParts()
    return useMutation({
        mutationFn: async ({ id, body }: { id: number | null; body: EstatePartInput }) =>
            (id == null ? await recoverRequestsApi.createPart(body) : await recoverRequestsApi.updatePart(id, body)).data.data,
        onSuccess: refresh,
    })
}
export function useDeleteEstatePart() {
    const refresh = useRefreshParts()
    return useMutation({ mutationFn: async (id: number) => (await recoverRequestsApi.deletePart(id)).data.data, onSuccess: refresh })
}
export function useOrderEstateParts() {
    const refresh = useRefreshParts()
    return useMutation({ mutationFn: async (ids: number[]) => (await recoverRequestsApi.orderParts(ids)).data.data, onSuccess: refresh })
}
